import { apiClient } from '@/shared/api';
import { safeRemove } from '../storage';
import { getReadyServiceWorker } from './serviceWorkerReady';

/**
 * 푸시 구독 endpoint 로컬 캐시 키. usePushSubscription 과 공유(단일 출처).
 */
export const PUSH_SUBSCRIBE_CACHE_KEY = 'axiom-push-endpoint';

/**
 * 이 기기의 푸시 구독을 서버·브라우저·로컬 캐시에서 모두 정리한다.
 *
 * 로그아웃/오프보딩 시 호출한다. 백엔드 구독 삭제는 endpoint+userId 매칭이므로
 * 반드시 auth 토큰이 유효한 동안(로그아웃 mutation 이전)에 호출해야 한다.
 *
 * 정리를 빠뜨리면 공유 기기(출퇴근 키오스크)에서 다음 사용자가 로그인해도 브라우저가
 * 같은 endpoint 를 재사용 → 로컬 캐시가 남아 재구독이 스킵 → 서버 userId 재할당이
 * 안 돼 이전 사용자에게 푸시가 계속 새는 cross-user 유출이 된다. 그래서 finally 로
 * 캐시를 반드시 비워, 서버 삭제가 실패하더라도 다음 재구독이 endpoint→새 userId 로
 * upsert 되도록 한다.
 */
export async function cleanupPushSubscription(): Promise<void> {
  try {
    // 서비스워커를 상한 안에 못 찾으면 브라우저 구독 해제는 건너뛰고 캐시 정리만 한다 —
    // 여기서 무한정 기다리면 이 함수를 첫 await 로 두는 로그아웃이 통째로 멈춘다.
    const reg = await getReadyServiceWorker();
    if (reg) {
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        // 서버 삭제와 브라우저 해제는 독립적으로 수행한다 — 서버 삭제가 실패해도(네트워크 등)
        // 브라우저 구독 해제는 반드시 시도해야 공유 기기에서 이전 사용자 푸시 수신이 즉시
        // 끊긴다. (서버 삭제만 실패하면 매핑이 잠시 남을 수 있으나, 브라우저 구독을 없애면
        // 다음 사용자 재구독이 endpoint→새 userId 로 upsert 되어 정리된다.)
        try {
          await apiClient.delete(
            `notifications/push/subscriptions/${encodeURIComponent(sub.endpoint)}`
          );
        } catch {
          // 서버 삭제 실패는 삼킨다 — 아래 브라우저 해제 + finally 캐시 정리로 유출을 차단.
        }
        await sub.unsubscribe();
      }
    }
  } finally {
    safeRemove(PUSH_SUBSCRIBE_CACHE_KEY);
  }
}
